import FbButton from 'components/FbUI/FbButton'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { MobileSearchSelectView } from 'components/MobileSearchSelectView'
import { MobileModal } from 'components/UI/Modal/MobileModal'
import { useState } from 'react'
import { PiPlusCircleBold } from 'react-icons/pi'
import {
  useGetVariantsCatalogInfinite,
  type VariantCatalog,
  type VariantsCatalogResponse,
} from 'services/products_api'

type Props = {
  title?: string
  onCancel: () => void
  onSubmit: (variantId: number | null, variantName: string | null) => void
  productId: number
  initialVariantId: number | null
}

export function MobileSelectProductVariantModal({
  title = 'Create A Deal',
  onCancel,
  onSubmit,
  productId,
  initialVariantId = null,
}: Props) {
  const filterStore = filterStoreRepo.getStore('SELECT_PRODUCT_VARIANT')
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(
    initialVariantId
  )
  const [selectedVariantName, setSelectedVariantName] = useState<string | null>(
    null
  )

  const params = useFilterParams(filterStore)

  const infiniteQuery = useGetVariantsCatalogInfinite(
    { product: [productId], match_status: 'matched', ...params },
    50,
    !!productId
  )

  return (
    <MobileModal
      header={
        <div className="p-2">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
            {title}
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Select Product Variant
          </h1>
        </div>
      }
      footer={
        <div>
          <div className="flex gap-4 p-2 justify-between">
            <div className="w-1/2">
              <FbButton
                className="w-full"
                type="button"
                size="mdlg"
                variant="secondary"
                onClick={onCancel}
              >
                Back
              </FbButton>
            </div>
            <div className="w-1/2">
              <FbButton
                className="w-full"
                type="button"
                size="mdlg"
                variant="primary"
                onClick={() => onSubmit(selectedVariantId, selectedVariantName)}
              >
                <PiPlusCircleBold style={{ fontSize: 18 }} />
                {selectedVariantId ? 'Confirm Variant' : 'Clear Variant'}
              </FbButton>
            </div>
          </div>
        </div>
      }
    >
      <MobileSearchSelectView<VariantsCatalogResponse, VariantCatalog>
        filterStoreIdentifier="SELECT_PRODUCT_VARIANT"
        initialSelectedIds={initialVariantId ? [initialVariantId] : []}
        infiniteQuery={infiniteQuery}
        onChange={(items) => {
          if (items.length > 0) {
            setSelectedVariantId(Number(items[items.length - 1].value))
            setSelectedVariantName(items[items.length - 1].label)
          } else {
            setSelectedVariantId(null)
            setSelectedVariantName(null)
          }
        }}
        extractItems={(page) =>
          page.results.map((v) => ({
            value: v.id,
            label: v.name,
            sku: v.sku,
            gtin: v.gtin,
            format_pack_size: v.format_pack_size,
          }))
        }
        extractCount={(page) => page.count}
        renderItem={(item) => {
          const sku = item.sku as string | undefined
          const gtin = item.gtin as string | undefined
          const formatPackSize = item.format_pack_size as string | undefined
          return (
            <>
              <div>{item.label}</div>
              <div className="text-[10px] text-gray-500 space-y-[1px]">
                {sku && <div>SKU: {sku}</div>}
                {gtin && <div>GTIN/UPC: {gtin}</div>}
                {formatPackSize && <div>Format: {formatPackSize}</div>}
              </div>
            </>
          )
        }}
      />
    </MobileModal>
  )
}
